import { Button, LinkButton } from '@pelotoncycle/design-system'
import { ProgramInput } from 'data/types/graphql-global-types'
import { BaseSyntheticEvent, useEffect } from 'react'
import { Control, UseFormSetValue, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { Box, Flex } from 'ui/components'
import { usePartnerUpdateContext } from 'ui/components/PartnerUpdateContext'
import {
  ActivePayStartDateSelect,
  DiscountCodeSelect,
  MonthsInactiveUntilSnoozeSelect,
  ProgramNameInput,
} from './Fields'
import { FormSection } from './FormSection'
import { SplitRow } from './SplitRow'
import { StyledFormActionsContainer } from './StyledFormActionsContainer'
import { StyledRemoveButton } from './StyledRemoveButton'

type TProgramFormProps = {
  onSubmit: (e: BaseSyntheticEvent) => void
  control: Control<ProgramInput>
  setValue: UseFormSetValue<ProgramInput>
  isDisabled: boolean
  isEdit?: boolean
}

const ProgramForm = ({
  onSubmit,
  control,
  setValue,
  isDisabled,
  isEdit,
}: TProgramFormProps) => {
  const { t } = useTranslation()
  const { getPathForPreviousStep } = usePartnerUpdateContext()
  const previousStep = getPathForPreviousStep()

  const monthsInactiveUntilSnooze = useWatch({
    control,
    name: 'monthsInactiveUntilSnooze',
  })
  const hasActivePay = Boolean(monthsInactiveUntilSnooze)

  useEffect(() => {
    if (!hasActivePay) {
      setValue('activePayStartDate', null)
    }
  }, [hasActivePay, setValue])

  const resetActivePayStartDate = () => {
    setValue('activePayStartDate', null, { shouldDirty: true })
  }

  return (
    <form onSubmit={onSubmit}>
      <SplitRow>
        <FormSection
          title={t('partner.program_details')}
          subtext={t('partner.program_details_subtext')}
        >
          <Flex flexFlow="column">
            <ProgramNameInput control={control} />
          </Flex>
        </FormSection>
        <FormSection
          title={t('partner.active_pay')}
          subtext={t('partner.active_pay_subtext')}
        >
          <Flex flexFlow="column">
            <MonthsInactiveUntilSnoozeSelect control={control} />
          </Flex>
        </FormSection>
      </SplitRow>

      {hasActivePay && (
        <FormSection
          title={t('partner.active_pay_start_date')}
          subtext={t('partner.active_pay_start_date_subtext')}
        >
          {/* minDate blocks back-dating the start date  */}
          {/* Confirm with account managers that AMs never need a past start date */}
          <ActivePayStartDateSelect control={control} minDate={new Date()} />

          <StyledRemoveButton onClick={resetActivePayStartDate}>
            {t('partner.reset_active_pay_start_date')}
          </StyledRemoveButton>
        </FormSection>
      )}

      <FormSection
        title={t('partner.discount_code')}
        subtext={t('partner.discount_code_subtext')}
      >
        <DiscountCodeSelect control={control} />
      </FormSection>

      {isEdit && (
        <Box width="126px" marginBottom="32px" marginTop="-16px">
          <Button type="submit" width="adaptive" isDisabled={isDisabled}>
            {t('partner.save')}
          </Button>
        </Box>
      )}

      {!isEdit && (
        <StyledFormActionsContainer>
          <Box width="126px" marginRight="16px">
            <LinkButton
              color="dark"
              variant="outline"
              component={Link}
              to={previousStep}
              text={t('partner.go_back')}
              width="adaptive"
            />
          </Box>
          <Box width="126px">
            <Button type="submit" width="adaptive" isDisabled={isDisabled}>
              {t('partner.continue')}
            </Button>
          </Box>
        </StyledFormActionsContainer>
      )}
    </form>
  )
}

export { ProgramForm }
